import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import apiService from '../../../../services/apiService';
import { Button } from '../../../../components/ui';
import { FaPencilAlt, FaArrowLeft } from 'react-icons/fa';

const breadcrumb = (
  <span>
    <span className="cursor-pointer hover:text-blue-600" onClick={() => window.history.back()}>
      Home
    </span> &nbsp;/&nbsp; 
    <span className="cursor-pointer hover:text-blue-600" onClick={() => window.history.back()}>
      Courses
    </span> &nbsp;/&nbsp; 
    <span className="text-gray-700">View</span>
  </span>
);

const ViewCourse = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('basic');

  useEffect(() => {
    const fetchCourse = async () => {
      try {
        setLoading(true);
        const data = await apiService.request(`/i/ims/course/${id}?populate=eduStandard`);
        setCourse(data);
      } catch (error) {
        console.error('Error fetching course:', error);
        alert('Failed to fetch course details');
        navigate('/course-programs/courses');
      } finally {
        setLoading(false);
      }
    };

    fetchCourse();
  }, [id, navigate]);

  if (loading || !course) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex justify-center items-center h-64">
          <div className="text-gray-500">Loading...</div>
        </div>
      </div>
    );
  }

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return dateString;
  };

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="p-6">
        <nav className="text-xs text-gray-500 mb-4" aria-label="Breadcrumb">
          {breadcrumb}
        </nav>
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-2xl font-bold text-gray-900">{course.name}</h1>
          <div className="flex space-x-2">
            <Button
              onClick={() => navigate('/course-programs/courses')}
              variant="outline"
              size="md"
              className="border-gray-300 text-gray-700"
              leftIcon={<FaArrowLeft />}
            >
              Back to List
            </Button>
            <Button
              onClick={() => navigate(`/course-programs/courses/edit/${id}`)}
              variant="primary"
              size="md"
              leftIcon={<FaPencilAlt />}
            >
              Edit
            </Button>
          </div>
        </div>
        
        {/* Status Badge */}
        <div className="mb-6">
          <span className={`px-3 py-1 rounded-full text-sm ${course.status ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
            {course.status ? 'Active' : 'Inactive'}
          </span>
        </div>
        
        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px space-x-8">
            <button
              onClick={() => setActiveTab('basic')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'basic'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Basic Details
            </button>
            <button
              onClick={() => setActiveTab('meta')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'meta'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Meta Tags
            </button>
          </nav>
        </div>
      </div>
      
      <div className="p-6">
        {activeTab === 'basic' && (
          <div>
            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold mb-2">Basic Information</h3>
                <div className="bg-gray-50 p-4 rounded">
                  <div className="mb-3">
                    <span className="block text-sm font-medium text-gray-500">Name</span>
                    <span className="block mt-1">{course.name}</span>
                  </div>
                  
                  <div className="mb-3">
                    <span className="block text-sm font-medium text-gray-500">Code</span>
                    <span className="block mt-1">{course.code || '-'}</span>
                  </div>
                  
                  <div className="mb-3">
                    <span className="block text-sm font-medium text-gray-500">URI</span>
                    <span className="block mt-1">{course.uri}</span>
                  </div>
                  
                  <div className="mb-3">
                    <span className="block text-sm font-medium text-gray-500">Primary Education Standard</span>
                    <span className="block mt-1">{course.eduStandard?.name || '-'}</span>
                  </div>
                  
                  <div className="mb-3">
                    <span className="block text-sm font-medium text-gray-500">Status</span>
                    <span className="block mt-1">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${course.status ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {course.status ? 'Active' : 'Inactive'}
                      </span>
                    </span>
                  </div>
                  
                  <div className="mb-3">
                    <span className="block text-sm font-medium text-gray-500">Order</span>
                    <span className="block mt-1">{course.order}</span>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold mb-2">Additional Details</h3>
                <div className="bg-gray-50 p-4 rounded">
                  {course.thumb && (
                    <div className="mb-3">
                      <span className="block text-sm font-medium text-gray-500">Thumbnail</span>
                      <div className="mt-2">
                        <img 
                          src={course.thumb.startsWith('http') ? course.thumb : `http://localhost:1333${course.thumb}`} 
                          alt={course.name} 
                          className="max-w-full h-auto rounded" 
                          style={{ maxHeight: '200px' }} 
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = '/placeholder-image.jpg';
                          }}
                        />
                      </div>
                    </div>
                  )}
                  
                  <div className="mb-3">
                    <span className="block text-sm font-medium text-gray-500">Education Standards</span>
                    <div className="mt-1">
                      {course.eduStandards && course.eduStandards.length > 0 ? (
                        <ul className="list-disc pl-5">
                          {course.eduStandards.map(standard => (
                            <li key={standard.id}>{standard.name}</li>
                          ))}
                        </ul>
                      ) : '-'}
                    </div>
                  </div>
                  
                  <div className="mb-3">
                    <span className="block text-sm font-medium text-gray-500">Created At</span>
                    <span className="block mt-1">{course.createdAt ? new Date(course.createdAt).toLocaleString() : '-'}</span>
                  </div>
                  
                  <div className="mb-3">
                    <span className="block text-sm font-medium text-gray-500">Updated At</span>
                    <span className="block mt-1">{course.updatedAt ? new Date(course.updatedAt).toLocaleString() : '-'}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {activeTab === 'meta' && (
          <div>
            <h3 className="text-lg font-semibold mb-2">Meta Tags</h3>
            <div className="bg-gray-50 p-4 rounded">
              {course.metaTags ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="mb-3">
                    <span className="block text-sm font-medium text-gray-500">OG Title</span>
                    <span className="block mt-1">{course.metaTags.ogTitle || '-'}</span>
                  </div>
                  
                  <div className="mb-3">
                    <span className="block text-sm font-medium text-gray-500">OG Description</span>
                    <span className="block mt-1">{course.metaTags.ogDescription || '-'}</span>
                  </div>
                  
                  <div className="mb-3">
                    <span className="block text-sm font-medium text-gray-500">Keywords</span>
                    <span className="block mt-1">{course.metaTags.keywords || '-'}</span>
                  </div>
                  
                  {course.metaTags.ogImage && (
                    <div className="mb-3">
                      <span className="block text-sm font-medium text-gray-500">OG Image</span>
                      <div className="mt-2">
                        <img 
                          src={course.metaTags.ogImage.startsWith('http') ? course.metaTags.ogImage : `http://localhost:1333${course.metaTags.ogImage}`} 
                          alt="OG Image" 
                          className="max-w-full h-auto rounded" 
                          style={{ maxHeight: '150px' }} 
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = '/placeholder-image.jpg';
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-gray-500">No meta tags available</div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ViewCourse;
